import { useState, useEffect } from 'react';
import ModalOverlay from '../../../components/ui/ModalOverlay/ModalOverlay';
import DocumentSelector from '../../../components/common/DocumentSelector/DocumentSelector';
import RichTextEditor from '../../../components/ui/RichTextEditor/RichTextEditor';
import s from '../CaseFilePage.module.css';
import api from '../../../services/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faTimes, faPaperclip, faDatabase } from '@fortawesome/free-solid-svg-icons';

/* ── Type definitions ── */
type EmailAttachment = {
  name: string;
  type: string;
  content?: string | ArrayBuffer;
};

type DocumentSelectorDocument = {
  DocumentID: string;
  FileName: string;
  DocumentType: string;
  S3Key: string;
  S3Bucket: string;
  GeneratedAt: string;
};

interface EmailDrafterModalProps {
  isOpen: boolean;
  onClose: () => void;
  caseRef: string;
  defaultTo: string;
  onSend: (data: {
    to: string;
    subject: string;
    body: string;
    attachments: EmailAttachment[];
    includeSignature?: boolean;
  }) => Promise<void>;
  isSending: boolean;
  isOutlookConnected: boolean;
  signature?: string;
}

interface AttachmentWithProgress {
  name: string;
  type: string;
  content?: string; // base64 for small files
  size: number; // original file size in bytes
  uploadProgress?: number; // 0-100 for large files
  uploadSessionId?: string; // Graph API upload session ID
  isLarge?: boolean; // true if using upload session
}

export default function EmailDrafterModal({
  isOpen,
  onClose,
  caseRef,
  defaultTo,
  onSend,
  isSending,
  isOutlookConnected,
  signature = '',
}: EmailDrafterModalProps) {
  const [to, setTo] = useState(defaultTo);
  const [subject, setSubject] = useState(`Case Reference: ${caseRef}`);
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState<AttachmentWithProgress[]>([]);
  const [isDocSelectorOpen, setIsDocSelectorOpen] = useState(false);
  const [sharedDraftId, setSharedDraftId] = useState<string | null>(null); // Shared draft for all large attachments

  // Sync recipient and signature when modal opens or props change
  useEffect(() => {
    if (isOpen) {
      setTo(defaultTo);
      setSubject(`Case Reference: ${caseRef}`);
      // Only set body if it's currently empty
      if (!body) {
        setBody('');
      }
      // Reset shared draft ID for new email
      setSharedDraftId(null);
    }
  }, [isOpen, defaultTo, caseRef, signature]);

  if (!isOpen) return null;

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB - Outlook limit
    const USE_UPLOAD_SESSION_THRESHOLD = 100 * 1024; // 100KB - use upload sessions for files > 100KB

    console.log('Processing file attachments:', files.length);

    for (const file of Array.from(files)) {
      // Check file size limit
      if (file.size > MAX_FILE_SIZE) {
        alert(
          `File "${file.name}" exceeds 20 MB limit (size: ${(file.size / (1024 * 1024)).toFixed(2)} MB). Please choose a smaller file.`,
        );
        continue;
      }

      const isLarge = file.size > USE_UPLOAD_SESSION_THRESHOLD;

      if (isLarge) {
        // Use upload session for better reliability and to avoid payload size issues
        console.log('Using upload session for file:', {
          name: file.name,
          size: file.size,
          sizeMB: (file.size / (1024 * 1024)).toFixed(2),
        });

        setAttachments((prev) => [
          ...prev,
          {
            name: file.name,
            type: file.type,
            size: file.size,
            uploadProgress: 0,
            isLarge: true,
          },
        ]);

        // Upload in background
        uploadLargeFile(file, file.name);
      } else {
        // Very small file - use base64 inline
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64Content = (reader.result as string).split(',')[1];
          console.log('Small file loaded:', {
            name: file.name,
            type: file.type,
            size: file.size,
            base64Length: base64Content?.length || 0,
          });

          // Validate base64 content
          if (!base64Content) {
            console.error('Failed to read file content:', file.name);
            alert(`Failed to read file: ${file.name}. Please try again.`);
            return;
          }

          setAttachments((prev) => [
            ...prev,
            {
              name: file.name,
              type: file.type,
              content: base64Content,
              size: file.size,
              uploadProgress: 100,
              isLarge: false,
            },
          ]);
        };
        reader.onerror = (error) => {
          console.error('FileReader error:', error);
          alert(`Error reading file: ${file.name}. Please try again.`);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const uploadLargeFile = async (file: File, attachmentName: string) => {
    try {
      // Create upload session via backend (reuse shared draft if it exists)
      const { data: sessionData } = await api.post('/api/emails/create-upload-session', {
        fileName: file.name,
        fileSize: file.size,
        draftId: sharedDraftId, // Pass existing draft ID if available
      });

      const { uploadUrl, sessionId } = sessionData;

      // Store the draft ID for reuse by other large files
      if (!sharedDraftId) {
        setSharedDraftId(sessionId);
      }

      // Upload in 320KB chunks
      const chunkSize = 320 * 1024;
      const totalChunks = Math.ceil(file.size / chunkSize);

      for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
        const start = chunkIndex * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const chunk = file.slice(start, end);

        // Upload chunk
        await fetch(uploadUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/octet-stream',
            'Content-Range': `bytes ${start}-${end - 1}/${file.size}`,
          },
          body: chunk,
        });

        // Update progress by matching attachment name
        const progress = Math.round(((chunkIndex + 1) / totalChunks) * 100);
        setAttachments((prev) =>
          prev.map((att) =>
            att.name === attachmentName
              ? { ...att, uploadProgress: progress, uploadSessionId: sessionId }
              : att,
          ),
        );
      }

      console.log('Large file upload complete:', file.name);
    } catch (err) {
      console.error('Failed to upload large file:', file.name, err);
      alert(`Failed to upload ${file.name}. Please try again.`);
      // Remove failed attachment by name
      setAttachments((prev) => prev.filter((a) => a.name !== attachmentName));
    }
  };

  const handleDocSelectorSelect = async (docs: DocumentSelectorDocument[]) => {
    console.log('Attaching system documents:', docs.length);
    const USE_UPLOAD_SESSION_THRESHOLD = 100 * 1024; // 100KB - consistent with handleFileChange

    for (const doc of docs) {
      try {
        console.log('Downloading document:', doc.FileName, doc.DocumentID);

        const res = await api.get(`/api/admin/documents/${doc.DocumentID}/download`, {
          responseType: 'blob',
        });

        const blob = res.data as Blob;
        const isLarge = blob.size > USE_UPLOAD_SESSION_THRESHOLD;

        console.log('Document downloaded:', {
          fileName: doc.FileName,
          blobSize: blob.size,
          blobType: blob.type,
          isLarge,
        });

        if (isLarge) {
          // Convert blob to file and use upload session
          const file = new File([blob], doc.FileName, { type: blob.type });
          setAttachments((prev) => [
            ...prev,
            {
              name: doc.FileName,
              type: blob.type || 'application/pdf',
              size: blob.size,
              uploadProgress: 0,
              isLarge: true,
            },
          ]);
          uploadLargeFile(file, doc.FileName);
        } else {
          // Very small file - use base64
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64Content = (reader.result as string).split(',')[1];
            console.log('Document converted to base64:', {
              fileName: doc.FileName,
              base64Length: base64Content?.length || 0,
            });
            setAttachments((prev) => [
              ...prev,
              {
                name: doc.FileName,
                type: blob.type || 'application/pdf',
                content: base64Content,
                size: blob.size,
                uploadProgress: 100,
                isLarge: false,
              },
            ]);
          };
          reader.onerror = (error) => {
            console.error('FileReader error for system doc:', doc.FileName, error);
          };
          reader.readAsDataURL(blob);
        }
      } catch (err) {
        console.error('Failed to download system doc:', doc.FileName, err);
      }
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    // Check if body has actual content (not just empty HTML tags)
    const hasContent = body && body.replace(/<[^>]*>/g, '').trim().length > 0;
    if (!to || !subject || !hasContent) {
      console.warn('Cannot send email: missing required fields', {
        hasTo: !!to,
        hasSubject: !!subject,
        hasContent,
      });
      return;
    }

    // Total size already validated (20MB limit)
    // Just log the details
    console.log('Sending email:', {
      to,
      subject,
      bodyLength: body.length,
      attachmentCount: attachments.length,
      totalSizeMB: totalSizeMB.toFixed(2),
      attachmentSizeMB: attachmentSizeMB.toFixed(2),
      bodySizeMB: bodySizeMB.toFixed(2),
      attachments: attachments.map((a) => ({
        name: a.name,
        type: a.type,
        hasContent: !!a.content,
        hasUploadSession: !!a.uploadSessionId,
        contentLength: a.content?.length || 0,
        sizeMB: (a.size / (1024 * 1024)).toFixed(2),
      })),
    });

    // Prepare attachments: separate inline from upload session
    const attachmentsForBackend = attachments.map((att) => {
      if (att.isLarge && att.uploadSessionId) {
        // Large file uploaded via session
        return {
          name: att.name,
          type: att.type,
          uploadSessionId: att.uploadSessionId,
          isLarge: true,
        };
      } else {
        // Small file with base64 content
        return {
          name: att.name,
          type: att.type,
          content: att.content,
          isLarge: false,
        };
      }
    });

    // We send includeSignature: true to let the backend handle the final attachment
    // The backend will check if it's already in the 'body' to avoid duplicates.
    await onSend({ to, subject, body, attachments: attachmentsForBackend, includeSignature: true });
    onClose();
  };

  // Calculate total payload size for validation and UI
  const totalAttachmentBytes = attachments.reduce((sum, a) => sum + a.size, 0);
  const attachmentSizeMB = totalAttachmentBytes / (1024 * 1024);
  const bodySizeMB = (body.length * 0.75) / (1024 * 1024);
  const totalSizeMB = attachmentSizeMB + bodySizeMB;
  const MAX_SIZE_MB = 20; // Outlook's limit
  const isOverLimit = totalSizeMB > MAX_SIZE_MB;
  const percentUsed = (totalSizeMB / MAX_SIZE_MB) * 100;
  const isNearLimit = percentUsed > 75;
  const hasIncompleteUploads = attachments.some((a) => a.isLarge && (a.uploadProgress || 0) < 100);

  return (
    <ModalOverlay title="New Email (Outlook API)" onClose={onClose} wide>
      <div className={s.emailDrafterContent}>
        {/* Action buttons at the top */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
            paddingBottom: '16px',
            borderBottom: '1px solid var(--border-primary)',
          }}
        >
          <button className={s.btnSecondary} onClick={onClose} disabled={isSending}>
            Cancel
          </button>
          <button
            className={s.btnPrimary}
            onClick={handleSend}
            disabled={
              isSending ||
              !to ||
              !subject ||
              !body ||
              body.replace(/<[^>]*>/g, '').trim().length === 0 ||
              !isOutlookConnected ||
              isOverLimit ||
              hasIncompleteUploads
            }
            title={
              isOverLimit
                ? 'Email size exceeds 20 MB limit - remove attachments'
                : hasIncompleteUploads
                  ? 'Waiting for file uploads to complete...'
                  : ''
            }
          >
            {isSending ? (
              'Sending...'
            ) : (
              <>
                <FontAwesomeIcon icon={faPaperPlane} />
                &nbsp;Send via Outlook
              </>
            )}
          </button>
        </div>

        <div className={s.drafterField}>
          <label>To:</label>
          <input
            type="email"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="recipient@example.com"
            disabled={true}
            className={s.drafterInputReadonly}
          />
        </div>

        <div className={s.drafterField}>
          <label>Subject:</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Email subject"
          />
        </div>

        <div className={s.drafterBodyField}>
          <label>Message Content:</label>
          <RichTextEditor
            content={body}
            onChange={setBody}
            placeholder="Type your message here..."
          />
        </div>

        {signature.trim() && (
          <div className={s.drafterField} data-testid="appended-signature-preview">
            <label>Signature Preview:</label>
            <div
              style={{
                padding: '14px 16px',
                border: '1px solid var(--border-primary)',
                borderRadius: '8px',
                background: 'var(--surface-secondary, var(--gray-50))',
              }}
            >
              <div
                style={{
                  fontSize: '0.75rem',
                  color: 'var(--text-muted, var(--gray-500))',
                  marginBottom: '10px',
                }}
              >
                This saved signature will be appended to the email when sent.
              </div>
              <div dangerouslySetInnerHTML={{ __html: signature }} />
            </div>
          </div>
        )}

        {attachments.length > 0 && (
          <div className={s.attachmentList}>
            {attachments.map((att, idx) => (
              <div key={idx} className={s.attachmentItem}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontWeight: 500 }}>{att.name}</span>
                    <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                      ({(att.size / (1024 * 1024)).toFixed(2)} MB)
                    </span>
                  </div>
                  {att.isLarge && att.uploadProgress !== undefined && att.uploadProgress < 100 && (
                    <div style={{ marginTop: '4px' }}>
                      <div
                        style={{
                          height: '4px',
                          backgroundColor: 'var(--gray-200)',
                          borderRadius: '2px',
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            height: '100%',
                            width: `${att.uploadProgress}%`,
                            backgroundColor: 'var(--primary-600)',
                            transition: 'width 0.3s ease',
                          }}
                        />
                      </div>
                      <span
                        style={{
                          fontSize: '0.75rem',
                          color: 'var(--text-muted)',
                          marginTop: '2px',
                        }}
                      >
                        Uploading... {att.uploadProgress}%
                      </span>
                    </div>
                  )}
                </div>
                <button onClick={() => removeAttachment(idx)}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className={s.drafterAttachments}>
          <input
            type="file"
            id="email-attach"
            multiple
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <button
            className={s.btnSecondary}
            onClick={() => document.getElementById('email-attach')?.click()}
          >
            <FontAwesomeIcon icon={faPaperclip} />
            &nbsp;Attach Local File
          </button>
          <button
            className={s.btnSecondary}
            onClick={() => setIsDocSelectorOpen(true)}
            style={{ marginLeft: '10px' }}
          >
            <FontAwesomeIcon icon={faDatabase} />
            &nbsp;Attach System Doc
          </button>

          {/* Size indicator */}
          {totalSizeMB > 0.01 && (
            <div
              style={{
                marginTop: '8px',
                fontSize: '0.85rem',
                color: isOverLimit ? '#dc2626' : isNearLimit ? '#f59e0b' : 'var(--text-muted)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <span>
                {isOverLimit ? '⚠️' : isNearLimit ? '⚡' : '📎'} Size: {totalSizeMB.toFixed(2)} MB /{' '}
                {MAX_SIZE_MB} MB ({percentUsed.toFixed(0)}%)
              </span>
              {isOverLimit && (
                <span style={{ fontWeight: 600 }}>- Too large! Remove attachments.</span>
              )}
            </div>
          )}

          <span className={s.hintText}>
            Sent via your Outlook. A copy is saved to S3 and the Document Ledger.
          </span>
        </div>

        {/* Connection status at the bottom */}
        <div
          style={{
            marginTop: '20px',
            paddingTop: '16px',
            borderTop: '1px solid var(--border-primary)',
            textAlign: 'center',
          }}
        >
          {!isOutlookConnected ? (
            <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
              ⚠️ Outlook is not connected.{' '}
              <a
                href="/preferences"
                style={{ color: 'var(--primary-600)', textDecoration: 'underline' }}
                onClick={(e) => {
                  e.preventDefault();
                  onClose();
                  window.location.href = '/preferences';
                }}
              >
                Go to Preferences to connect
              </a>
            </div>
          ) : (
            <span style={{ color: '#28a745', fontSize: '0.9rem' }}>
              <FontAwesomeIcon icon={faPaperPlane} /> Outlook Connected
            </span>
          )}
        </div>
      </div>
      <DocumentSelector
        isOpen={isDocSelectorOpen}
        onClose={() => setIsDocSelectorOpen(false)}
        caseRef={caseRef}
        onSelect={handleDocSelectorSelect}
      />
    </ModalOverlay>
  );
}
